import api from './api';

export interface PositionBreak {
  id: string;
  fund_id: string;
  break_type: string;
  break_date: string;
  detected_at: string;
  security_id?: string;
  security_name?: string;
  expected_value: number;
  actual_value: number;
  difference: number;
  difference_percentage: number;
  severity: string;
  status: string;
  resolved_at?: string;
  resolution_notes?: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
}

export interface PositionBreaksResponse {
  breaks: PositionBreak[];
  total: number;
  skip: number;
  limit: number;
}

export interface BreakSummary {
  total_breaks: number;
  open_breaks: number;
  resolved_breaks: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_type: {
    reconciliation: number;
    valuation: number;
    settlement: number;
  };
}

class PositionBreakService {
  async getPositionBreaks(params?: {
    fund_id?: string;
    break_type?: string;
    severity?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    skip?: number;
    limit?: number;
  }): Promise<PositionBreaksResponse> {
    const response = await api.get('/position-breaks', { params });
    return response.data;
  }

  async getPositionBreak(breakId: string): Promise<PositionBreak> {
    const response = await api.get(`/position-breaks/${breakId}`);
    return response.data;
  }

  async detectBreaks(
    fundId: string,
    breakDate?: string,
    breakType?: string
  ): Promise<{ breaks_detected: number; breaks: PositionBreak[] }> {
    const response = await api.post('/position-breaks/detect', null, {
      params: {
        fund_id: fundId,
        break_date: breakDate,
        break_type: breakType,
      },
    });
    return response.data;
  }

  async resolveBreak(
    breakId: string,
    resolutionNotes: string,
    userId?: string
  ): Promise<PositionBreak> {
    const response = await api.put(`/position-breaks/${breakId}/resolve`, {
      resolution_notes: resolutionNotes,
      user_id: userId,
    });
    return response.data;
  }

  async getBreakSummary(fundId?: string): Promise<BreakSummary> {
    const response = await api.get('/position-breaks/summary/stats', {
      params: fundId ? { fund_id: fundId } : {},
    });
    return response.data;
  }

  async getOpenBreaksForFund(fundId: string): Promise<PositionBreak[]> {
    const response = await api.get(`/position-breaks/fund/${fundId}/open`);
    return response.data.open_breaks || [];
  }
}

export const positionBreakService = new PositionBreakService();

